import React from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';

import {CollapsiblePanel, EditTariffType, ITariff, normalizeTariffData, tariffAPI} from 'entities/tariff';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import Checkbox from 'shared/ui/Checkbox/Checkbox';
import InputName from 'shared/ui/Input/InputName';
import InputNumber from 'shared/ui/Input/InputNumber/InputNumber';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';

import styles from './TariffForm.module.scss';

interface ITariffFormProps{
    onSuccess: () => void,
    item ?: ITariff,
    onClose: () => void,
}

const TariffForm: React.FC<ITariffFormProps> = ({onSuccess, item, onClose}) => {
    const {register, handleSubmit, formState, control, setValue} = useForm<EditTariffType>({
        mode: 'onChange', defaultValues: {
            price: item?.price ?? 0,
            period: item?.period ?? 0,
            is_archive: item?.is_archive ?? false,
            is_recommended: item?.is_recommended ?? false,

            monitor_max_count_limit: item?.monitor_max_count_limit ?? 0,
            server_max_count_limit: item?.server_max_count_limit ?? 0,
            domain_monitor_max_count_limit: item?.domain_monitor_max_count_limit ?? 0,
            port_monitor_max_count_limit: item?.port_monitor_max_count_limit ?? 0,
            location_max_count_limit: item?.location_max_count_limit ?? 0,
            monitor_min_check_interval_limit: item?.monitor_min_check_interval_limit ?? 1,
            server_alert_rules_max_count_limit: item?.server_alert_rules_max_count_limit ?? 0,
            status_pages_max_count_limit: item?.status_pages_max_count_limit ?? 0,
            reports_max_count_limit: item?.reports_max_count_limit ?? 0,
            status_page_hide_banner_enabled: item?.status_page_hide_banner_enabled ?? false,
            report_hide_banner_enabled: item?.report_hide_banner_enabled ?? false,

            monitor_ssl_enabled: item?.monitor_ssl_enabled ?? false,
            monitor_domain_watch_enabled: item?.monitor_domain_watch_enabled ?? false,
            monitor_ssl_redirect_watch_enabled: item?.monitor_ssl_redirect_watch_enabled ?? false,
            monitor_robots_txt_watch_enabled: item?.monitor_robots_txt_watch_enabled ?? false,
            monitor_rkn_watch_enabled: item?.monitor_rkn_watch_enabled ?? false,
            monitor_extended_methods: item?.monitor_extended_methods ?? false,
            monitor_parse_errors: item?.monitor_parse_errors ?? false,
            monitor_screenshot_on_error: item?.monitor_screenshot_on_error ?? false,
            monitor_frequent_checking: item?.monitor_frequent_checking ?? false,
            monitor_content_enabled: item?.monitor_content_enabled ?? false,
            monitor_http_basic_auth_enabled: item?.monitor_http_basic_auth_enabled ?? false,
            monitor_custom_user_agent_enabled: item?.monitor_custom_user_agent_enabled ?? false,
            monitor_custom_host_enabled: item?.monitor_custom_host_enabled ?? false,

            telegram_chats_max_count_limit: item?.telegram_chats_max_count_limit ?? 0,
            max_chats_max_count_limit: item?.max_chats_max_count_limit ?? 0,
            webhook_max_count_limit: item?.webhook_max_count_limit ?? 0,
            additional_emails_max_count_limit: item?.additional_emails_max_count_limit ?? 0,
        },
    });
    const {errors, isValid, isDirty} = formState;

    const [addTariff, {isLoading: isLoadingCreate}] = tariffAPI.useAddTariffMutation();
    const [updateTariff, {isLoading: isLoadingUpdate}] = tariffAPI.useUpdateTariffMutation();

    const isNew = !item;

    const onSubmit: SubmitHandler<EditTariffType> = async(values) => {
        const data = normalizeTariffData(values);
        try {
            if (isNew) {
                await addTariff(data).unwrap();
            } else {
                await updateTariff({ id: item?.id, ...data }).unwrap();
            }
            onSuccess();
        } catch (e) {
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBodyWrapper>
                <FormGroupWithError
                    error={errors?.name?.message}
                    label="Название"
                >
                    <InputName
                        defaultValue={item?.name}
                        control={control}
                        name="name"
                        placeholder="Укажите название"
                        register={register}
                        required
                    />
                </FormGroupWithError>
                <div className={styles.grid2Cols}>
                    <FormGroupWithError
                        error={errors?.price?.message}
                        label="Стоимость, руб"
                    >
                        <InputNumber
                            min={0}
                            name="price"
                            defaultValue={item?.price ?? 0}
                            control={control}
                            setValue={setValue}
                        />
                    </FormGroupWithError>
                    <FormGroupWithError
                        error={errors?.period?.message}
                        label="Срок действия, дней"
                    >
                        <InputNumber
                            min={0}
                            name="period"
                            defaultValue={item?.period ?? 0}
                            control={control}
                            setValue={setValue}
                        />
                    </FormGroupWithError>
                </div>
                <div className={styles.grid2Cols}>
                    <FormGroupWithError>
                        <Checkbox
                            textStyle={styles.checkbox}
                            {...register('is_archive')}
                        >
                        Архивный
                        </Checkbox>
                    </FormGroupWithError>
                    <FormGroupWithError>
                        <Checkbox
                            textStyle={styles.checkbox}
                            {...register('is_recommended')}
                        >
                        Рекомендуемый
                        </Checkbox>
                    </FormGroupWithError>
                </div>
            </ModalBodyWrapper>
            <ModalBodyWrapper>
                <CollapsiblePanel title="Мониторинг" defaultExpanded={false}>
                    <div className={styles.grid2Cols}>
                        <FormGroupWithError
                            error={errors?.monitor_max_count_limit?.message}
                            label="Кол-во сайтов"
                        >
                            <InputNumber
                                min={0}
                                name="monitor_max_count_limit"
                                defaultValue={item?.monitor_max_count_limit ?? 0}
                                control={control}
                                setValue={setValue}
                            />
                        </FormGroupWithError>
                        <FormGroupWithError
                            error={errors?.server_max_count_limit?.message}
                            label="Кол-во серверов"
                        >
                            <InputNumber
                                min={0}
                                name="server_max_count_limit"
                                defaultValue={item?.server_max_count_limit ?? 0}
                                control={control}
                                setValue={setValue}
                            />
                        </FormGroupWithError>
                        <FormGroupWithError
                            error={errors?.domain_monitor_max_count_limit?.message}
                            label="Кол-во доменов"
                        >
                            <InputNumber
                                min={0}
                                name="domain_monitor_max_count_limit"
                                defaultValue={item?.domain_monitor_max_count_limit ?? 0}
                                control={control}
                                setValue={setValue}
                            />
                        </FormGroupWithError>
                        <FormGroupWithError
                            error={errors?.port_monitor_max_count_limit?.message}
                            label="Кол-во портов"
                        >
                            <InputNumber
                                min={0}
                                name="port_monitor_max_count_limit"
                                defaultValue={item?.port_monitor_max_count_limit ?? 0}
                                control={control}
                                setValue={setValue}
                            />
                        </FormGroupWithError>
                        <FormGroupWithError
                            error={errors?.location_max_count_limit?.message}
                            label="Кол-во локаций"
                        >
                            <InputNumber
                                min={0}
                                max={100}
                                name="location_max_count_limit"
                                defaultValue={item?.location_max_count_limit ?? 0}
                                control={control}
                                setValue={setValue}
                            />
                        </FormGroupWithError>
                        <FormGroupWithError
                            error={errors?.monitor_min_check_interval_limit?.message}
                            label="Мин. интервал"
                        >
                            <InputNumber
                                min={1}
                                name="monitor_min_check_interval_limit"
                                defaultValue={item?.monitor_min_check_interval_limit ?? 1}
                                control={control}
                                setValue={setValue}
                            />
                        </FormGroupWithError>
                        <FormGroupWithError
                            error={errors?.server_alert_rules_max_count_limit?.message}
                            label="Кол-во правил уведомлений (серверы)"
                        >
                            <InputNumber
                                min={0}
                                name="server_alert_rules_max_count_limit"
                                defaultValue={item?.server_alert_rules_max_count_limit ?? 0}
                                control={control}
                                setValue={setValue}
                            />
                        </FormGroupWithError>
                    </div>
                </CollapsiblePanel>
            </ModalBodyWrapper>
            <ModalBodyWrapper>
                <CollapsiblePanel title="Отчёты" defaultExpanded={false}>
                    <div className={styles.grid2Cols}>
                        <FormGroupWithError
                            error={errors?.status_pages_max_count_limit?.message}
                            label="Кол-во страниц статуса"
                        >
                            <InputNumber
                                min={0}
                                name="status_pages_max_count_limit"
                                defaultValue={item?.status_pages_max_count_limit ?? 0}
                                control={control}
                                setValue={setValue}
                            />
                        </FormGroupWithError>
                        <FormGroupWithError
                            error={errors?.reports_max_count_limit?.message}
                            label="Кол-во отчётов"
                        >
                            <InputNumber
                                min={0}
                                name="reports_max_count_limit"
                                defaultValue={item?.reports_max_count_limit ?? 0}
                                control={control}
                                setValue={setValue}
                            />
                        </FormGroupWithError>
                    </div>
                    <div className={styles.grid2Cols}>
                        <FormGroupWithError>
                            <Checkbox
                                textStyle={styles.checkbox}
                                {...register('status_page_hide_banner_enabled')}
                            >
                                Скрыть баннер (статус)
                            </Checkbox>
                        </FormGroupWithError>
                        <FormGroupWithError>
                            <Checkbox
                                textStyle={styles.checkbox}
                                {...register('report_hide_banner_enabled')}
                            >
                                Скрыть баннер (отчёты)
                            </Checkbox>
                        </FormGroupWithError>
                    </div>
                </CollapsiblePanel>
            </ModalBodyWrapper>
            <ModalBodyWrapper>
                <CollapsiblePanel title="Проверки" defaultExpanded={false}>
                    <div className={styles.grid2Cols}>
                        <FormGroupWithError>
                            <Checkbox
                                textStyle={styles.checkbox}
                                {...register('monitor_ssl_enabled')}
                            >
                                Проверка SSL
                            </Checkbox>
                        </FormGroupWithError>
                        <FormGroupWithError>
                            <Checkbox
                                textStyle={styles.checkbox}
                                {...register('monitor_ssl_redirect_watch_enabled')}
                            >
                                Проверка редиректа с http на https
                            </Checkbox>
                        </FormGroupWithError>
                        <FormGroupWithError>
                            <Checkbox
                                textStyle={styles.checkbox}
                                {...register('monitor_domain_watch_enabled')}
                            >
                                Мониторинг домена
                            </Checkbox>
                        </FormGroupWithError>
                        <FormGroupWithError>
                            <Checkbox
                                textStyle={styles.checkbox}
                                {...register('monitor_extended_methods')}
                            >
                                Расширенные методы мониторинга
                            </Checkbox>
                        </FormGroupWithError>
                        <FormGroupWithError>
                            <Checkbox
                                textStyle={styles.checkbox}
                                {...register('monitor_robots_txt_watch_enabled')}
                            >
                                Проверка файла robots.txt
                            </Checkbox>
                        </FormGroupWithError>
                        <FormGroupWithError>
                            <Checkbox
                                textStyle={styles.checkbox}
                                {...register('monitor_parse_errors')}
                            >
                                Разбор проблем мониторинга
                            </Checkbox>
                        </FormGroupWithError>
                        <FormGroupWithError>
                            <Checkbox
                                textStyle={styles.checkbox}
                                {...register('monitor_screenshot_on_error')}
                            >
                                Скриншот страницы ошибки
                            </Checkbox>
                        </FormGroupWithError>
                        <FormGroupWithError>
                            <Checkbox
                                textStyle={styles.checkbox}
                                {...register('monitor_frequent_checking')}
                            >
                                Учащенный мониторинг
                            </Checkbox>
                        </FormGroupWithError>
                        <FormGroupWithError>
                            <Checkbox
                                textStyle={styles.checkbox}
                                {...register('monitor_content_enabled')}
                            >
                                Проверка контента
                            </Checkbox>
                        </FormGroupWithError>
                        <FormGroupWithError>
                            <Checkbox
                                textStyle={styles.checkbox}
                                {...register('monitor_http_basic_auth_enabled')}
                            >
                                HTTP Basic Auth
                            </Checkbox>
                        </FormGroupWithError>
                        <FormGroupWithError>
                            <Checkbox
                                textStyle={styles.checkbox}
                                {...register('monitor_custom_user_agent_enabled')}
                            >
                                Кастомный User-Agent
                            </Checkbox>
                        </FormGroupWithError>
                        <FormGroupWithError>
                            <Checkbox
                                textStyle={styles.checkbox}
                                {...register('monitor_custom_host_enabled')}
                            >
                                Кастомный Host
                            </Checkbox>
                        </FormGroupWithError>
                        <FormGroupWithError>
                            <Checkbox
                                textStyle={styles.checkbox}
                                {...register('monitor_rkn_watch_enabled')}
                            >
                                Мониторинг РКН
                            </Checkbox>
                        </FormGroupWithError>
                    </div>
                </CollapsiblePanel>
            </ModalBodyWrapper>
            <ModalBodyWrapper>
                <CollapsiblePanel title="Уведомления" defaultExpanded={false}>
                    <div className={styles.grid2Cols}>
                        <FormGroupWithError
                            error={errors?.telegram_chats_max_count_limit?.message}
                            label="Кол-во Telegram чатов"
                        >
                            <InputNumber
                                min={0}
                                name="telegram_chats_max_count_limit"
                                defaultValue={item?.telegram_chats_max_count_limit ?? 0}
                                control={control}
                                setValue={setValue}
                            />
                        </FormGroupWithError>
                        <FormGroupWithError
                            error={errors?.max_chats_max_count_limit?.message}
                            label="Кол-во MAX чатов"
                        >
                            <InputNumber
                                min={0}
                                name="max_chats_max_count_limit"
                                defaultValue={item?.max_chats_max_count_limit ?? 0}
                                control={control}
                                setValue={setValue}
                            />
                        </FormGroupWithError>
                        <FormGroupWithError
                            error={errors?.additional_emails_max_count_limit?.message}
                            label="Кол-во дополнительных E-mail"
                        >
                            <InputNumber
                                min={0}
                                name="additional_emails_max_count_limit"
                                defaultValue={item?.additional_emails_max_count_limit ?? 0}
                                control={control}
                                setValue={setValue}
                            />
                        </FormGroupWithError>
                        <FormGroupWithError
                            error={errors?.webhook_max_count_limit?.message}
                            label="Кол-во вебхуков"
                        >
                            <InputNumber
                                min={0}
                                name="webhook_max_count_limit"
                                defaultValue={item?.webhook_max_count_limit ?? 0}
                                control={control}
                                setValue={setValue}
                            />
                        </FormGroupWithError>
                    </div>
                </CollapsiblePanel>
            </ModalBodyWrapper>
            <ModalBlockButton
                primaryButtonText={isNew ? 'Добавить' : 'Изменить'}
                primaryButtonType="submit"
                primaryButtonLoading={isLoadingCreate || isLoadingUpdate}
                primaryButtonDisabled={!(isValid && (isDirty || isNew))}
                secondaryButtonOnClick={onClose}
            />
        </form>
    );
};

export default TariffForm;